import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';

import { useSnackNotificationContext } from '@sorare/core/src/contexts/snackNotification';
import { useSportContext } from '@sorare/core/src/contexts/sport';
import { useMutation } from '@sorare/core/src/hooks/graphql/useMutation';
import { useIsMobileApp } from '@sorare/core/src/hooks/useIsMobileApp';
import { useSendUserEngagementIncrement } from '@sorare/core/src/hooks/useSendUserEngagementIncrement';
import { formatGqlErrors } from '@sorare/core/src/lib/gql';
import { UserEngagementScoreIncrement_Source } from '@sorare/core/src/protos/webview/channel/native_messages';

import type {
  ClaimRewardsMutation,
  ClaimRewardsMutationVariables,
} from './__generated__/useClaimRewards.graphql';

const CLAIM_REWARDS_MUTATION = gql`
  mutation ClaimRewardsMutation($input: claimRewardsInput!, $sport: Sport!) {
    claimRewards(input: $input) {
      currentUser {
        slug
        unclaimedSo5Rewards(sport: $sport) {
          slug
        }
        unopenedProbabilisticBundles(sport: $sport, first: 100) {
          totalCount
        }
        xpBalances: inGameCurrencyBalances(
          sport: $sport
          inGameCurrencies: [
            COMMON_XP
            LIMITED_XP
            RARE_XP
            SUPER_RARE_XP
            UNIQUE_XP
          ]
        ) {
          amount
          currency
          capAlmostReached
        }
      }
      so5Rewards {
        slug
        aasmState
        so5Fixture {
          slug
          mySo5Rewards {
            slug
            aasmState
          }
        }
      }
      errors {
        message
        code
      }
    }
  }
` as TypedDocumentNode<ClaimRewardsMutation, ClaimRewardsMutationVariables>;

export const useClaimRewards = () => {
  const [claim, { loading }] = useMutation(CLAIM_REWARDS_MUTATION);
  const { showNotification } = useSnackNotificationContext();
  const { isAndroidApp, postMessage } = useIsMobileApp();
  const { sendUserEngagementIncrement } = useSendUserEngagementIncrement();
  const { sport } = useSportContext();

  return [
    async (input: ClaimRewardsMutationVariables['input']) => {
      const result = await claim({
        variables: {
          input,
          sport: sport!,
        },
      });

      const errors = result.data?.claimRewards?.errors || [];

      if (errors.length) {
        showNotification('errors', { errors: formatGqlErrors(errors) });
        return errors;
      }
      if (isAndroidApp) postMessage('rewardClaimed');
      sendUserEngagementIncrement(
        25,
        UserEngagementScoreIncrement_Source.CLAIM_REWARD
      );
      return null;
    },
    { loading },
  ] as const;
};
